import Axios, { AxiosError, AxiosRequestConfig } from "axios";
import { REACT_APP_API_ACCOUNTS } from "../../config";

/**
 * Keeps the stored session alive.
 *
 * Every service reads its Bearer token straight out of `tygo_user` in
 * localStorage at call time. Nothing used to react when that token expired:
 * the calls simply started coming back 401 and the page rendered empty until
 * the user logged in again by hand.
 *
 * The accounts MS exposes `RefreshAuthenticationToken`, which takes the old
 * token in the query string and hands back a fresh one. It is anonymous and
 * parses the JWT itself rather than going through the auth middleware, so it
 * still accepts a token that has already expired — which is what makes the
 * recovery below possible.
 *
 * Two layers:
 *  - `initAuthSession()` refreshes on boot when the stored token is expired or
 *    about to be, so the common case never produces a 401 at all;
 *  - the response interceptor catches any 401 that slips through, refreshes
 *    once, and replays the original request.
 */

const STORAGE_KEY = "tygo_user";
const REFRESH_PATH = "/api/AuthControllers/RefreshAuthenticationToken";

/**
 * Requests that must never trigger a refresh: they are how a session is
 * obtained in the first place, and a 401 from them is a real answer (bad OTP,
 * unknown user) rather than an expired token. The refresh call is in the list
 * so a failing refresh cannot recurse into itself.
 */
const AUTH_PATHS = [
  REFRESH_PATH,
  "/api/AuthControllers/Login",
  "/api/AuthControllers/VerifyCode",
  "/api/AuthControllers/Register",
];

/** Refresh this long before the stated expiry rather than waiting for it. */
const EXPIRY_SKEW_MS = 2 * 60 * 1000;

type StoredUser = {
  token?: string;
  tokenExpiryDate?: string;
  [key: string]: any;
};

export const readStoredUser = (): StoredUser => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    // A corrupt entry is indistinguishable from no session at all.
    return {};
  }
};

/**
 * Merges the new credentials into the stored user instead of replacing it:
 * the same object carries `userId`, the profile and the flags other pages set,
 * and a refresh must not drop them.
 */
const storeCredentials = (data: any) => {
  const token = data?.token || data?.accessToken;
  if (!token) return null;

  const user = readStoredUser();
  user.token = token;
  if (data.tokenExpiryDate) user.tokenExpiryDate = data.tokenExpiryDate;
  if (data.userId) user.userId = data.userId;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  return token as string;
};

const isAuthPath = (url?: string) =>
  !!url && AUTH_PATHS.some((path) => url.includes(path));

/** `true` only when there is an expiry we can read and it has passed. */
const isExpired = (user: StoredUser, skewMs = EXPIRY_SKEW_MS) => {
  if (!user.tokenExpiryDate) return false;
  const expiry = new Date(user.tokenExpiryDate).getTime();
  if (Number.isNaN(expiry)) return false;
  return expiry - skewMs <= Date.now();
};

/**
 * Ends the session locally and sends the user to sign in. Called only once the
 * refresh itself has failed, i.e. the session cannot be recovered.
 */
const endSession = () => {
  localStorage.removeItem(STORAGE_KEY);

  const path = window.location.pathname;
  // Already on a sign-in screen (or sent there a moment ago by a sibling
  // request): redirecting again would loop and wipe the form.
  if (path.startsWith("/login") || path.startsWith("/sign")) return;

  // /login2 (SignIn) is the sign-in screen that honours `redirect` and brings
  // the user back to the page they were on; /login ignores it and drops them
  // on the home page.
  window.location.assign(
    `/login2?redirect=${encodeURIComponent(path + window.location.search)}`
  );
};

/**
 * One refresh at a time. A page typically fires several authorised calls at
 * once, so without this every one of them would start its own refresh and all
 * but the last would immediately invalidate the token the others just stored.
 */
let inFlight: Promise<string | null> | null = null;

export const refreshAuthToken = (): Promise<string | null> => {
  if (inFlight) return inFlight;

  const { token } = readStoredUser();
  if (!token) return Promise.resolve(null);

  inFlight = Axios.post(
    REACT_APP_API_ACCOUNTS + REFRESH_PATH,
    // The endpoint takes the token in the query string and ignores the body,
    // but it is fronted by a gateway that answers 411 to a POST arriving with
    // no Content-Length — which is what a browser sends for a null body, and
    // Content-Length is a forbidden header we cannot set ourselves. An empty
    // JSON object is the smallest body that always carries one.
    {},
    {
      params: { token },
      // Anonymous endpoint: sending the dead token as a Bearer header as well
      // only risks it being rejected before it lands.
      headers: { "Content-Type": "application/json" },
    }
  )
    .then((res) => storeCredentials(res.data))
    // Any failure means the session is unrecoverable — including the 500 the
    // MS returns for a token it can parse but no longer recognises.
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** Replaces the Bearer header on a retried request, in either casing. */
const applyToken = (config: AxiosRequestConfig, token: string) => {
  const headers: any = config.headers || {};
  delete headers.authorization;
  delete headers.Authorization;
  headers.Authorization = `Bearer ${token}`;
  config.headers = headers;
};

let interceptorInstalled = false;

export const installAuthInterceptor = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  Axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original: any = error.config;

      if (
        error.response?.status !== 401 ||
        !original ||
        // Refreshed once already and still refused: the new token is not the
        // problem, so fail rather than loop.
        original._retriedAfterRefresh ||
        isAuthPath(original.url) ||
        // No session to refresh. Page guards already send these users to login.
        !readStoredUser().token
      ) {
        return Promise.reject(error);
      }

      const token = await refreshAuthToken();
      if (!token) {
        endSession();
        return Promise.reject(error);
      }

      original._retriedAfterRefresh = true;
      applyToken(original, token);
      return Axios.request(original);
    }
  );
};

/**
 * Call once on app boot, before anything fetches. Installs the interceptor and,
 * when the stored token is already past its expiry, renews it up front so the
 * first page load does not have to fail a request to discover it.
 */
export const initAuthSession = async () => {
  installAuthInterceptor();

  const user = readStoredUser();
  if (user.token && isExpired(user)) {
    await refreshAuthToken();
  }
};
